import React from 'react'
import { Avatar, Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

export const ROW_HEIGHT = 72
export const LEFT_COLUMN_WIDTH = 300

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  if (!parts.length) return '?'
  return `${parts[0]?.[0] || ''}${parts[1]?.[0] || parts[0]?.[1] || ''}`.toUpperCase()
}

function TruncatedTextTooltip({ title, children, typographySx = {}, variant, noWrap = true }) {
  const textRef = React.useRef(null)
  const [isTruncated, setIsTruncated] = React.useState(false)

  React.useEffect(() => {
    const node = textRef.current
    if (!node) return

    const checkOverflow = () => {
      setIsTruncated(node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight)
    }

    checkOverflow()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkOverflow) : null

    resizeObserver?.observe(node)
    window.addEventListener('resize', checkOverflow)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', checkOverflow)
    }
  }, [title])

  return (
    <Tooltip title={isTruncated ? title || '' : ''} arrow placement="top" disableHoverListener={!isTruncated}>
      <Typography ref={textRef} sx={typographySx} variant={variant} noWrap={noWrap}>
        {children}
      </Typography>
    </Tooltip>
  )
}

function ProjectCell({ row, isExpanded, onToggleProject }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.25,
        height: '100%',
        backgroundColor: '#FFFFFF',
        borderLeft: '3px solid #F2C94C',
      }}
    >
      <IconButton size="small" onClick={() => onToggleProject(row.projectId)} sx={{ ml: -0.5 }}>
        {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
      </IconButton>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F2C94C', flexShrink: 0, mt: 0.25 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <TruncatedTextTooltip
          title={row.project?.name || ''}
          typographySx={{ fontWeight: 600, color: '#344054', fontSize: 15, lineHeight: 1.2 }}
        >
          {row.project?.name}
        </TruncatedTextTooltip>
        <Typography variant="caption" sx={{ color: '#98A2B3', fontSize: 12 }}>
          {row.tasksCount} visible task{row.tasksCount !== 1 ? 's' : ''}
        </Typography>
      </Box>
    </Stack>
  )
}

function TaskCell({ row }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        pl: 5.5,
        pr: 1.5,
        height: '100%',
        backgroundColor: '#FFFFFF',
      }}
    >
      <Avatar src={row.task?.assigneeAvatarUrl || undefined} sx={{ width: 26, height: 26, fontSize: 11 }}>
        {getInitials(row.task?.assigneeName)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
        <TruncatedTextTooltip
          title={row.task?.name || ''}
          typographySx={{
            fontWeight: 500,
            color: '#344054',
            fontSize: 14,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.task?.name}
        </TruncatedTextTooltip>
        <TruncatedTextTooltip
          title={row.task?.assigneeName || ''}
          variant="caption"
          typographySx={{
            color: '#98A2B3',
            fontSize: 11,
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          {row.task?.assigneeName}
        </TruncatedTextTooltip>
      </Box>

      <Chip
        size="small"
        label={row.task?.statusLabel}
        sx={{
          height: 22,
          backgroundColor:
            row.task?.statusKey === 'overdue' || row.task?.statusKey === 'needs_rework'
              ? row.task?.timelineItem?.barColor
              : '#FFFFFF',
          color: row.task?.timelineItem?.textColor,
          border: `1px solid ${row.task?.timelineItem?.borderColor || '#D0D5DD'}`,
          '& .MuiChip-label': { px: 1, fontSize: 11, fontWeight: 500 },
        }}
        style={{ flexShrink: 0 }}
      />
    </Stack>
  )
}

export function GanttTaskListHeader() {
  return (
    <Box
      sx={{
        minWidth: LEFT_COLUMN_WIDTH,
        width: LEFT_COLUMN_WIDTH,
        backgroundColor: '#FFFFFF',
        position: 'sticky',
        left: 0,
        zIndex: 3,
      }}
    >
      <Box
        sx={{
          height: 64,
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #D0D5DD',
          backgroundColor: '#F9FAFB',
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#667085', }}>
          Projects & Tasks
        </Typography>
      </Box>
    </Box>
  )
}

export function GanttTaskListCell({ row, expandedProjectIds, onToggleProject }) {
  return (
    <Box
      sx={{
        minWidth: LEFT_COLUMN_WIDTH,
        width: LEFT_COLUMN_WIDTH,
        height: ROW_HEIGHT + 1,
        boxSizing: 'border-box',
        borderBottom: '1px solid #EAECF0',
        borderRight: '1px solid #EAECF0',
        backgroundColor: '#FFFFFF',
        flexShrink: 0,
        position: 'sticky',
        left: 0,
        zIndex: 2,
      }}
    >
      {row.rowType === 'project' ? (
        <ProjectCell
          row={row}
          isExpanded={expandedProjectIds.has(row.projectId)}
          onToggleProject={onToggleProject}
        />
      ) : (
        <TaskCell row={row} />
      )}
    </Box>
  )
}

export default function GanttTaskList(props) {
  return <GanttTaskListCell {...props} />
}
